import { useState, useRef, useMemo, useEffect } from "react";
import { noOptionList } from "src/components/collections/helpers";
import { shouldShowSearchField } from "@hyro-components/fields/utils";

const ADD_NEW = "add_new";

const withSelectedFirst = (list, value) => {
  const index = list.findIndex((option) => option.key === value);
  if (index <= 0) return list;
  return [list[index], ...list.slice(0, index), ...list.slice(index + 1)];
};

export const useSingleSelect = ({ options, field, noOptionsMessage, onFocus, onPopperClose, onAddNewCollection, onChange, onSearchChange, isAsync, open, value, loading, selectedFirst = false }) => {
  const [menuOpen, setMenuOpen] = useState(open);
  const [searchText, setSearchText] = useState("");
  const inputRef = useRef(null);

  const optionsExist = options.length > 0;
  const valueExistsInOptions = options.some(option => option.key === value);
  const showPlaceholder = !valueExistsInOptions && !loading && !(isAsync && value);
  // Async: the parent filters server-side, so the search field is always available;
  // sync keeps the length-threshold behavior.
  const showSearch = isAsync ? true : shouldShowSearchField(options);

  // Async: options are already the server's filtered page — never client-filter them, or
  // matches beyond the fetched page would be hidden. Route the query up via onSearchChange.
  const handleSearch = isAsync ? (onSearchChange ?? (() => {})) : setSearchText;

  const filteredOptions = useMemo(() => {
    const resolveOptions = () => {
      if (isAsync) {
        return optionsExist ? options : noOptionList(field?.spec?.display_key, noOptionsMessage);
      }
      if (!optionsExist) return noOptionList(field?.spec?.display_key, noOptionsMessage);
      const optionsFiltered = options.filter(option =>
        option.display_key.toLowerCase().includes(searchText.toLowerCase())
      );
      return optionsFiltered.length === 0
        ? noOptionList(field?.spec?.display_key, noOptionsMessage)
        : optionsFiltered;
    };

    const resolved = resolveOptions();
    return selectedFirst ? withSelectedFirst(resolved, value) : resolved;
  }, [options, searchText, field?.spec?.display_key, optionsExist, noOptionsMessage, isAsync, selectedFirst, value]);

  useEffect(() => {
    if (menuOpen) {
      onFocus?.(inputRef.current.node);
    } else {
      onPopperClose?.();
    }
  }, [menuOpen, onFocus, onPopperClose]);

  const handleOpen = () => {
    requestAnimationFrame(() => setMenuOpen(true));
  };

  const handleChange = ({ target }) => {
    if (target.value === ADD_NEW) {
      onAddNewCollection();
    } else {
      onChange?.(target.value);
    }
  };

  return {
    menuOpen,
    setMenuOpen,
    searchText,
    inputRef,
    filteredOptions,
    optionsExist,
    showSearch,
    showPlaceholder,
    handleOpen,
    handleChange,
    setSearchText,
    handleSearch,
    ADD_NEW
  };
};
